/**
 * ZenMarkup Privacy Analytics — Map module.
 *
 * SVG world map rendering, country coloring, and tooltip on hover.
 * Pan/zoom handled by map-controls.js.
 */

import * as mapTooltip from './map-tooltip.js';
import * as mapControls from './map-controls.js';
import { formatCompact } from './utils.js';

/**
 * Blue color stops — 5 levels for clear visual distinction.
 */
const FILL_STOPS = ['#C7D9F0', '#A8C4E8', '#89AFE0', '#6B9AD8', '#4D85D0'];

let container = null;
let svg = null;

/**
 * Pick a fill color based on count relative to max.
 *
 * @param {number} count   Visitor count for this country.
 * @param {number} max     Highest visitor count across all countries.
 * @return {string} Hex color from FILL_STOPS.
 */
function getFillColor(count, max) {
	if (max <= 0 || count <= 0) {
		return FILL_STOPS[0];
	}

	const ratio = count / max;
	const idx = Math.min(Math.floor(ratio * FILL_STOPS.length), FILL_STOPS.length - 1);
	return FILL_STOPS[idx];
}

/**
 * Color country paths based on visitor data and bind hover events.
 *
 * @param {Array} countries  Array of { country: string, count: number }.
 */
function colorCountries(countries) {
	if (!svg) {
		return;
	}

	const paths = svg.querySelectorAll('path[data-name]');
	paths.forEach(function (path) {
		path.style.fill = '';
		path.classList.remove('zmpa-country-active');
		path.removeAttribute('data-count');
	});

	if (!countries || !countries.length) {
		return;
	}

	let max = 0;
	const countMap = {};

	countries.forEach(function (c) {
		countMap[c.country] = c.visitors;
		if (c.visitors > max) {
			max = c.visitors;
		}
	});

	paths.forEach(function (path) {
		const name = path.getAttribute('data-name');
		const count = countMap[name] || 0;

		if (count > 0) {
			path.style.fill = getFillColor(count, max);
			path.classList.add('zmpa-country-active');
			path.setAttribute('data-count', count);
		}
	});
}

/** Bind mouseover/mousemove/mouseout on all country paths. */
function bindHover() {
	svg.addEventListener('mouseover', function (e) {
		const path = e.target.closest('path[data-name]');
		if (path) {
			mapTooltip.show(e, path.getAttribute('data-name'), path.getAttribute('data-count'), container);
		}
	});

	svg.addEventListener('mousemove', function (e) {
		const path = e.target.closest('path[data-name]');
		if (!path) {
			mapTooltip.hide();
			return;
		}
		mapTooltip.show(e, path.getAttribute('data-name'), path.getAttribute('data-count'), container);
	});

	svg.addEventListener('mouseout', function (e) {
		if (e.target.closest('path[data-name]')) {
			mapTooltip.hide();
		}
	});
}

/**
 * Render the world map with country visitor data.
 *
 * @param {Object} data  Response from API fetchStats().
 */
export function render(data) {
	container = document.querySelector('[data-el="map-container"]');
	if (!container) {
		return;
	}

	svg = container.querySelector('svg');
	if (!svg) {
		return;
	}

	const countries = data.countries || [];

	let max = 0;
	countries.forEach(function (c) {
		if (c.visitors > max) {
			max = c.visitors;
		}
	});

	mapTooltip.create(container);
	bindHover();
	colorCountries(countries);
	if (max > 0) {
		mapTooltip.buildLegend(container, max);
	}
	mapControls.init(container, svg);

	const statsEl = document.querySelector('[data-el="map-stats"]');
	if (statsEl && data.visitors !== undefined) {
		const v = formatCompact(data.visitors || 0);
		const p = formatCompact(data.pageviews || 0);
		statsEl.textContent = v + ' visitors \u00b7 ' + p + ' pageviews';
	}
}
